/* eslint-disable no-restricted-syntax */
import axios from 'axios';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { reportUtils } from './report-utils';
import { AI_ACTION_NAME_MAP, AI_ACTION_TYPE, API_AI_ACTIONS } from './constants';
import {
    AiRequestOptions,
    CommonResp,
    GenerateChartReq,
    GenerateChartResp,
    GenerateImageReq,
    GenerateImageResp,
    ImagePositionReq,
    ImagePositionResp,
    SearchImageReq,
    SearchImageReqParams,
    SearchImageResp,
    UploadImageReq,
    UploadImageReqParams,
    UploadImageResp,
} from './api.interface';
import { LogType, reportErrorToTam } from '@src/slide/infrastructure/module/dependent/report/js-error/tam-report';

/**
 * 搜图接口
 *
 * 搜图结果存在缓存，相同关键词的结果相同
 * file_id 用于做容量统计
 */
export const searchImages = async (params: SearchImageReqParams) => {
    const data = await postAiRequest<SearchImageReq, SearchImageResp>({
        actionType: AI_ACTION_TYPE.ACTION_TYPE_SEARCH_IMAGE,
        RequestData: {
            search_image: {
                ...params,
                file_id: SlideDataServices.ClientVarsService()?.getClientVars().globalPadId ?? '',
            },
        },
    });
    return data?.result;
};

/**
 * 搜图/生图 图片上传接口
 *
 * 外链上传可能会失败，因此需要传入缩略图进行上传兜底
 * file_id 用于做容量统计
 */
export const uploadImageByImageInfo = async (params: UploadImageReqParams) => {
    const data = await postAiRequest<UploadImageReq, UploadImageResp>({
        actionType: AI_ACTION_TYPE.ACTION_TYPE_UPLOAD_IMAGE,
        RequestData: {
            upload_image: {
                image: { ...params },
                file_id: SlideDataServices.ClientVarsService()?.getClientVars().globalPadId ?? '',
            },
        },
    });
    return data?.result;
};

/**
 * 生图接口
 */
export const generateImage = async (params: GenerateImageReq) => {
    const data = await postAiRequest<{ generate_image: GenerateImageReq }, GenerateImageResp>({
        actionType: AI_ACTION_TYPE.ACTION_TYPE_GENERATE_IMAGE,
        RequestData: {
            generate_image: {
                ...params,
            },
        },
    });
    return data?.result;
};

/**
 * 关键词提取接口
 */
export const getImagePosition = async (params: ImagePositionReq) => {
    const data = await postAiRequest<{ gen_image_identifier: ImagePositionReq }, ImagePositionResp>({
        actionType: AI_ACTION_TYPE.ACTION_TYPE_IMAGE_POSITION,
        RequestData: {
            gen_image_identifier: {
                ...params,
            },
        },
        onGetResponse: (reporter, request, response) => {
            const content = response.result.image_identifier.positioned_keyword;
            if (content === request.gen_image_identifier.input) {
                reporter.fail({ ext1: 'NO_IMAGE_POSITION' });
                console.error('getImagePosition useless: ', JSON.stringify(response));
            }
        },
    });
    return data?.result.image_identifier.positioned_keyword;
};


/**
 * 关键词提取接口
 */
export const generateChart = async (params: GenerateChartReq) => {
    const data = await postAiRequest<{ gen_slide_chart: GenerateChartReq }, GenerateChartResp>({
        actionType: AI_ACTION_TYPE.ACTION_TYPE_GENERATE_CHART,
        RequestData: {
            gen_slide_chart: {
                ...params,
            },
        },
        onGetResponse: (reporter, request, response, traceId) => {
            if (!response.result.gen_slide_chart.gen_chart) {
                reporter.fail({ ext1: 'NO_CHART' });
                reportErrorToTam({ message: `generate chart failed ${traceId}` }, LogType.ERROR);

                return { fail: true };
            }
        },
    });
    return data?.result.gen_slide_chart;
};

/**
 * 接口通用请求流程
 */
const postAiRequest = async <ReqType, RespType extends CommonResp>(
    options: AiRequestOptions<ReqType, RespType>,
): Promise<RespType | null> => {
    const { actionType, RequestData, onGetResponse } = options;
    const report = reportUtils.start(AI_ACTION_TYPE[actionType]);
    try {
        const responseData = await axios<RespType>({
            method: 'POST',
            url: `${API_AI_ACTIONS}?xsrf=${getXSRF()}`,
            data: {
                action_type: actionType,
                action_name: AI_ACTION_NAME_MAP[actionType],
                ...RequestData,
            },
            timeout: 60 * 1000,
        });
        const { data, headers } = responseData;
        if (data.retcode !== 0) {
            report.fail({ ext1: `code-${data.retcode}` });
            console.error('getImagePosition error: ', JSON.stringify(data));
            return null;
        }
        const { fail } = onGetResponse?.(report, RequestData, data, headers['X-Trace-Id']) ?? {};
        if (fail) {
            return null;
        }
        report.success();
        return data;
    } catch (err) {
        report.fail({ ext1: `http-${err.code}` });
        console.error('getImagePosition error: ', err);
        return null;
    }
};
